import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThunkActionDispatch } from 'redux-thunk';
import { isRejected } from '@reduxjs/toolkit';
import { LoadingScreen } from '@mesa-labs/mesa-ui';

import { useDispatch, useSelector } from '../../redux/hooks';
import { AuthenticationGroup, setupCognito } from '../../redux/slices/auth';
import { buildSignInPath, SIGN_IN_PATH } from '../../utils/signInRedirect';

type CognitoRouteProps = {
  children: React.ReactElement;
  redirectToSignIn?: boolean | string;
  authorizedRoles?: AuthenticationGroup[];
};

function CognitoRoute({
  children,
  redirectToSignIn = true,
  authorizedRoles = [],
}: CognitoRouteProps): React.ReactElement {
  const dispatch = useDispatch() as ThunkActionDispatch<any>;
  const navigate = useNavigate();
  const location = useLocation();
  /** @TODO this boolean should also depend on whether
   * or not the current auth user is an Operator */
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const roles = useSelector((state) => state.auth.roles);
  const [ready, setReady] = useState(false);

  // Reads window.location rather than the render-time location: auth checks resolve asynchronously
  // (and from several triggers), so a stale location would overwrite the saved redirect.
  const redirectToSignInPage = useCallback(() => {
    if (window.location.pathname === SIGN_IN_PATH) {
      return;
    }
    navigate(buildSignInPath(window.location), { replace: true });
  }, [navigate]);

  const checkAuth = useCallback(async (forceReload: boolean) => {
    const response = await dispatch(setupCognito({ forceRefresh: false, forceReload }));
    if (isRejected(response)) {
      redirectToSignInPage();
    }
  }, [dispatch, redirectToSignInPage]);

  // auth setup and listeners
  useEffect(() => {
    (async () => {
      await checkAuth(false);
      setReady(true);
    })();
  }, [isLoggedIn]);

  // Re-check auth when the user returns to the tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkAuth(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkAuth]);

  // handle authorization on route location update
  useEffect(() => {
    if (!ready || authorizedRoles.length === 0) {
      return;
    }

    // an authenticated user always has at least one internal role (getRBAC
    // signs out anyone without one), so empty roles means auth/persisted
    // state hasn't resolved yet on this render, not that access is denied
    if (!roles || roles.length === 0) {
      return;
    }

    // user is considered authorized to view this page
    // if they have some role that intersects with an
    // authorized role for this route
    const authorized = roles.some((userRole) => authorizedRoles.some((authorizedRole) => authorizedRole === userRole));

    if (!authorized) {
      navigate('/', { replace: true });
    }
  }, [ready, authorizedRoles, roles, location.pathname]);

  // handle redirect to sign-in if user is not logged in
  useEffect(() => {
    if (!ready) {
      return;
    }

    if (!isLoggedIn && redirectToSignIn) {
      if (typeof redirectToSignIn === 'string') {
        navigate(redirectToSignIn, { replace: true });
      } else {
        redirectToSignInPage();
      }
    }
  }, [ready, isLoggedIn]);

  return ready ? children : <LoadingScreen />;
}

export default CognitoRoute;
